import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import VillaCard from './VillaCard';
import EmotionalFilters from './EmotionalFilters';
import { 
  ArrowLeft, 
  MapPin, 
  Home, 
  Euro, 
  Search, 
  Filter,
  Sun,
  Thermometer,
  Wind
} from 'lucide-react';
import { destinationRegions } from '../data/destinations';
import { sampleVillas } from '../data/mock';

const DestinationDetail = () => {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('price');
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    // Find destination across all regions
    let foundDestination = null;
    let regionInfo = null;
    
    Object.entries(destinationRegions).forEach(([regionKey, region]) => {
      const dest = region.destinations.find(d => d.id === destinationId);
      if (dest) {
        foundDestination = dest;
        regionInfo = region;
      }
    });

    if (foundDestination && regionInfo) {
      setDestination({ ...foundDestination, region: regionInfo });
    } else {
      navigate('/');
    }
  }, [destinationId, navigate]);

  if (!destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la destination...</p>
        </div>
      </div>
    );
  }

  // Mock villas for this destination (in real app, would filter by destination)
  const destinationVillas = sampleVillas.filter(villa => {
    const matchesEmotion = !selectedEmotion || villa.moodPrimary === selectedEmotion;
    const matchesSearch = !searchQuery || 
      villa.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      villa.emotionalTagline.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesEmotion && matchesSearch;
  });

  const handleToggleFavorite = (villaId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(villaId)) {
        newFavorites.delete(villaId);
      } else {
        newFavorites.add(villaId);
      }
      return newFavorites;
    });
  };

  const handleViewVilla = (villa) => {
    navigate(`/villa/${villa.id}`);
  };

  // Mock weather data
  const weatherData = {
    temperature: 28,
    condition: 'Ensoleillé',
    humidity: 65,
    windSpeed: 12
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux destinations
            </Button>
            
            <div className="flex items-center gap-4">
              <Badge 
                variant="outline" 
                className="px-3 py-1 bg-blue-50 border-blue-200 text-blue-800"
              >
                {destination.region.name}
              </Badge>
              
              <Badge 
                variant="outline" 
                className="px-3 py-1 bg-amber-50 border-amber-200 text-amber-800"
              >
                {destination.villaCount} villas disponibles
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${destination.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        </div>
        
        <div className="relative z-10 flex items-end h-full">
          <div className="max-w-7xl mx-auto px-6 pb-16 text-white w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
              {/* Destination Info */}
              <div>
                <div className="flex items-center gap-2 mb-4 opacity-90">
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg font-light">{destination.country}</span>
                </div>
                
                <h1 
                  className="text-6xl md:text-7xl font-light mb-6 tracking-wide"
                  style={{ 
                    fontFamily: 'Playfair Display, serif',
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {destination.name}
                </h1>
                
                <p 
                  className="text-xl opacity-90 italic mb-8 max-w-2xl leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {destination.description}
                </p>
                
                <div className="flex items-center gap-6 text-lg">
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    <span>{destination.villaCount} villas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Euro className="w-5 h-5" />
                    <span>{destination.priceRange}</span>
                  </div>
                </div>
              </div>

              {/* Weather Card */}
              <div className="lg:justify-self-end">
                <Card className="bg-white/10 backdrop-blur-md border-white/20 p-6 text-white max-w-sm">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Sun className="w-5 h-5 text-yellow-400" />
                    Météo Actuelle
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="opacity-80">Température</span>
                      <div className="flex items-center gap-1">
                        <Thermometer className="w-4 h-4" />
                        <span className="font-semibold">{weatherData.temperature}°C</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="opacity-80">Conditions</span>
                      <span className="font-semibold">{weatherData.condition}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="opacity-80">Vent</span>
                      <div className="flex items-center gap-1">
                        <Wind className="w-4 h-4" />
                        <span className="font-semibold">{weatherData.windSpeed} km/h</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-light mb-4 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Villas à {destination.name}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez notre sélection exclusive de villas dans cette destination d'exception
            </p>
          </div>

          {/* Search and Sort */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher une villa..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-80 pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white/80 backdrop-blur-sm"
            >
              <option value="price">Trier par prix</option>
              <option value="name">Trier par nom</option>
              <option value="capacity">Trier par capacité</option>
            </select>
          </div>

          {/* Emotional Filters */}
          <EmotionalFilters 
            selectedEmotion={selectedEmotion}
            onEmotionChange={setSelectedEmotion}
          />
        </div>
      </section>

      {/* Villas Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Results Summary */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="px-4 py-2 text-base">
              {destinationVillas.length} villa{destinationVillas.length > 1 ? 's' : ''} trouvée{destinationVillas.length > 1 ? 's' : ''}
            </Badge>
          </div>

          {/* Villa Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinationVillas.map((villa) => (
              <VillaCard
                key={villa.id}
                villa={villa}
                onViewDetails={handleViewVilla}
                onToggleFavorite={handleToggleFavorite}
                isFavorite={favorites.has(villa.id)}
              />
            ))}
          </div>

          {destinationVillas.length === 0 && (
            <div className="text-center py-16">
              <p className="text-xl text-gray-500 mb-4">Aucune villa trouvée pour cette recherche</p>
              <Button 
                variant="outline"
                onClick={() => {
                  setSelectedEmotion(null);
                  setSearchQuery("");
                }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Destination Highlights */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-light mb-12 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Pourquoi Choisir {destination.name} ?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">
                Authenticité Préservée
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Une destination qui a su préserver son caractère unique tout en offrant le luxe moderne.
              </p>
            </Card>

            <Card className="p-8 bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">
                Excellence Culinaire
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Restaurants étoilés et chefs privés pour une expérience gastronomique inoubliable.
              </p>
            </Card>

            <Card className="p-8 bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">
                Exclusivité Garantie
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Accès privilégié aux meilleures adresses et expériences réservées à notre clientèle.
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DestinationDetail;